import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PageResponse } from 'src/app/auth/base-capacitor-http';
import { TrailerUpdate, YardUpdate } from 'src/app/dialogs/location/location.component';
import { Move } from 'src/app/services/app/moves.service';
import { Trailer, TrailerSearchParams, TrailerSearchType, TrailerService, TrailerStatus, TrailerUpdateOption } from 'src/app/services/app/trailer.service';
import { Yard, YardSearchParams, YardSearchType, YardService, YardStatus } from 'src/app/services/app/yard.service';
import { UtilitieService } from 'src/app/services/utils/utilities.service';
import { UpdateMessage, UpdateMessageType, WebsocketService } from 'src/app/services/utils/websocket.service';

@Component({
  selector: 'yard-view',
  templateUrl: './yard-view.component.html',
  styleUrls: ['./yard-view.component.css'],
})
export class YardViewComponent implements OnInit, OnDestroy {

  yardPage: PageResponse<Yard>;
  yardList: Yard[] = [];
  allYardList: Yard[] = [];
  lostTrailers: Trailer[] = [];
  pageSearchParams: YardSearchParams;
  yardStatus: string[] = [];
  searchOptions: { display: string, value: string }[] = [{ display: 'Status', value: 'status' }, { display: 'Trailer', value: 'trailer' }, { display: 'Location', value: 'location' }];
  selectedSearchOption: string;
  private destroy$: Subject<void> = new Subject<void>();
  isLoading: boolean = false;


  constructor(
    private yardService: YardService,
    private trailerService: TrailerService,
    private utilService: UtilitieService,
    private webSocketService: WebsocketService
  ) { }

  async statusDropDownSearch() {

    this.yardList = [];
    this.pageSearchParams.page = 0;
    // Now update your model manually
    switch (this.selectedSearchOption) {

      case 'status':
        this.pageSearchParams.type = YardSearchType.STATUS;
        break;

      case 'trailer':
        this.pageSearchParams.type = YardSearchType.TRAILER;
        this.pageSearchParams.search = '';
        break;

      case 'location':
        this.pageSearchParams.type = YardSearchType.TRAILER;
        this.pageSearchParams.search = '';
        break;

      default:
        this.pageSearchParams.type = YardSearchType.ALL;
        break;
    }

    await this.loadData(this.pageSearchParams.page);
  }

  searchByTrailer() {
    this.yardList = [];
    this.loadData(this.pageSearchParams.page);
  }

  async updateYard(update: YardUpdate) {
    await this.yardService.updateYard(update.update, update.yard);
  }

  async trailerUpdate(update: TrailerUpdate) {
    await this.trailerService.updateTrailer(update.update, update.trailer);
  }

  async markTrailerAsLost(yard: Yard) {
    const result = await this.utilService.openConfirmationDialog('Lost trailer', "Are you sure you want to mark this trailer as lost?");
    if (result) {
      const update = await this.yardService.updateYard('LOST_TRAILER', yard);
      this.utilService.openSnackBar('Trailer marked as missing', '');
    }
  }

  async checkout(yard: Yard) {
    const result = await this.utilService.openConfirmationDialog('Trailer checkout', "Are you sure you want to checkout this trailer?");
    if (result) {
      const update = await this.trailerService.updateTrailer(TrailerUpdateOption.CHECKOUT, yard.trailer);
      this.utilService.openSnackBar("Trailer has been checked out", "")
    }
  }

  async updateTrailer(trailer: Trailer) {
    const result = await this.trailerService.updateTrailer(TrailerUpdateOption.UPDATE, trailer);
    this.utilService.openSnackBar('Trailer has been updated', '');
  }
  /**
   * This button is a toggle that will changes the yeard status to out of service i 
   * not already in out of service and 
   * @param yard 
   */
  async markYardOutOfService(yard: Yard) {

    const result = await this.utilService.openConfirmationDialog('Update yard status', 'Are you sure you want to update the yard status? This will cancel any assignments associated with this yard location.');

    if (result) {
      yard = await this.yardService.updateYard('UPDATE', yard);
      this.utilService.openSnackBar('Yard status has been updated!', '');
    }

  }

  getDisableYardToolTip(yard: Yard): string {
    if (yard.status === YardStatus.OUT_OF_SERVICE) {
      return 'Mark yard in service';
    } else {
      return 'Mark yard as out of service';
    }
  }

  getDisableYardColor(yard: Yard): string {
    if (yard.status === YardStatus.OUT_OF_SERVICE) {
      return 'primary';
    } else {
      return 'warn';
    }
  }


  onScroll(event: Event) {
    const element = event.target as HTMLElement;

    // Calculate the scroll position
    const { scrollTop, scrollHeight, clientHeight } = element;

    // Buffer: Trigger 50px before the actual end to ensure smooth loading
    const buffer = 50;

    // Check if we are near the bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - buffer;

    if (isAtBottom) {
      // Prevent duplicate calls if already loading

      this.loadMore();
      setTimeout(() => {
        this.loadMore();
      }, 1000);

    }
  }

  async loadMore() {
    if (this.isLoading || this.yardPage.currentPage >= this.yardPage.totalPages) {
      return;
    }
    this.isLoading = true;
    const next = this.yardPage.currentPage + 1;
    this.loadData(next);
  }

  async loadData(pageNumber: number) {
    this.pageSearchParams.page = pageNumber;
    this.yardPage = await this.yardService.searchYards(this.pageSearchParams);
    this.yardPage.data.forEach(yard => {
      if (yard.trailerId == null) {
        yard.trailer = new Trailer();
      }
      this.yardList.push(yard);
    });

    this.isLoading = false;
  }

  async getAllLostTrailers() {
    const trailerSearchParams: TrailerSearchParams = {
      page: 0,
      size: 10,
      type: TrailerSearchType.TRAILER,
      search: TrailerStatus.LOST
    }
    const response = await this.trailerService.searchTrailers(trailerSearchParams);
    this.lostTrailers = response.data;
  }

  listenToUpdates() {
    this.webSocketService.getUpdateMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe(update => {
        this.processUpdate(update);
      });
  }

  private processUpdate(update: UpdateMessage) {
    if (!update) {
      return;
    }

    switch (update.type) {

      case UpdateMessageType.MOVE:

        this.processMoveUpdate(update);
        break;

      case UpdateMessageType.TRAILER:
        this.processTrailerUpdate(update);
        break;

      case UpdateMessageType.YARD:
        let yard: Yard = JSON.parse(update.data);

        let index = this.yardList.findIndex(y => y.id === yard.id);
        this.yardList[index] = yard;

        break;

      default:
        break;

    }
  }

  processMoveUpdate(update: UpdateMessage) {
    let move: Move = JSON.parse(update.data);

    let start: number = this.yardList.findIndex(y => y.id === move.startYardId);
    let end: number = this.yardList.findIndex(y => y.id === move.endYardId);
    this.yardList[start] = move.start;
    this.yardList[end] = move.end;
  }

  processTrailerUpdate(update: UpdateMessage) {
    // find trailer in yard locations and update info
    let trailer = JSON.parse(update.data);
    let index: number;
    switch (update.type) {
      case TrailerUpdateOption.LOST:
        this.lostTrailers.push(trailer)
        break;
      case TrailerUpdateOption.UPDATE:
        if (trailer.location === TrailerStatus.LOST) {
          index = this.lostTrailers.findIndex(t => t.id === trailer.id);
          if (index > -1) {
            this.lostTrailers[index] = trailer;
          }
          return;
        }

        index = this.allYardList.findIndex(y => y.trailerId === trailer.id);
        if (index > -1) {
          this.allYardList[index].trailer = trailer;
          this.allYardList[index].trailerId = trailer.id;
        }

        break;

      case TrailerUpdateOption.DELETE:
      case TrailerUpdateOption.CHECKOUT:
        if (trailer.location === TrailerStatus.LOST) {
          index = this.lostTrailers.findIndex(t => t.id === trailer.id);
          if (index > -1) {
            this.lostTrailers.splice(index, 1);
          }
          return;
        }

        index = this.allYardList.findIndex(y => y.trailerId === trailer.id);
        if (index > -1) {
          this.allYardList[index].trailer = null;
          this.allYardList[index].trailerId = null;
          this.allYardList[index].status = YardStatus.EMPTY;
        }
        break;
      case TrailerUpdateOption.CREATE:

        if (trailer.location === TrailerStatus.LOST) {
          this.lostTrailers.push(trailer);
        }

        break;
      default:

        break;
    }
  }


  async ngOnInit(): Promise<void> {
    this.utilService.updateLoading(true);

    this.pageSearchParams = new YardSearchParams();
    this.pageSearchParams.search = 'ALL';
    this.yardPage = new PageResponse();
    this.yardList = [];
    this.allYardList = [];

    this.yardStatus = this.yardService.getAllYardStatus();
    this.yardStatus.unshift('ALL');
    const lastIndex = this.searchOptions.length - 1;
    this.selectedSearchOption = this.searchOptions[0].value;
    await this.loadData(0);

    await this.getAllLostTrailers();
    this.utilService.updateLoading(false);
    this.listenToUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


}
